import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FullPage } from '@/components/common/FullPage'
import {
  ProviderForm,
  type ProviderFormValues,
} from '@/components/features/customers/ProviderForm'
import { useCreateProvider, useSetProviderPractices } from '@/hooks'
import { useToast } from '@/providers/ToastProvider'
import { friendlyNpiSaveError } from '@/lib/npi-duplicate'
import styles from './providers_.new.module.css'

export const Route = createFileRoute('/_authenticated/providers_/new')({
  component: NewProviderPage,
})

const friendlyCreateError = (err: unknown): string =>
  friendlyNpiSaveError(err, 'provider', 'Failed to add provider')

function NewProviderPage() {
  const { showToast } = useToast()
  const navigate = useNavigate()
  const createProvider = useCreateProvider()
  const setProviderPractices = useSetProviderPractices()

  return (
    <FullPage title="Add Provider">
      {close => {
        const handleSubmit = async (values: ProviderFormValues) => {
          try {
            const created = await createProvider.mutateAsync({
              practice_profile_id: null,
              prefix: values.prefix || null,
              first_name: values.first_name,
              last_name: values.last_name,
              credential: values.credential || null,
              npi: values.npi || null,
              npi_validated_at: values.npi_validated_at || null,
              taxonomy: values.taxonomy || null,
              phone: values.phone || null,
              business_email: values.business_email || null,
              address_line1: values.address_line1 || null,
              address_line2: values.address_line2 || null,
              city: values.city || null,
              state: values.state || null,
              postal_code: values.postal_code || null,
              address_validated_at: values.address_validated_at || null,
            })
            const practiceProfileIds = values.practice_profile_ids ?? []
            if (practiceProfileIds.length > 0) {
              await setProviderPractices.mutateAsync({
                providerId: created.id,
                practiceProfileIds,
              })
            }
            showToast('Provider added', 'success')
            navigate({ to: '/providers' })
          } catch (err) {
            showToast(friendlyCreateError(err), 'error')
          }
        }

        return (
          <div className={styles.page}>
            <ProviderForm
              isOpen
              embedded
              mode="create"
              title="Add Provider"
              showPracticePicker
              isSubmitting={createProvider.isPending}
              onSubmit={handleSubmit}
              onClose={close}
            />
          </div>
        )
      }}
    </FullPage>
  )
}
